import { useState, useEffect } from 'react';
import { X, Users, ScrollText, Activity, ShieldCheck, Inbox } from 'lucide-react';
import { UsersTab } from './manage/UsersTab';
import { AuditLogTab } from './manage/AuditLogTab';
import { TrackerLogTab } from './manage/TrackerLogTab';
import { AccessRequestsTab } from './manage/AccessRequestsTab';
import { PendingRequestsTab } from './manage/PendingRequestsTab';
import { canSeeManageTab, visibleManageTabs, type ManageTabId } from '../utils/manageAccess';
interface ManageModalProps {
  accessLevel: AdobeImsAccessLevel;
  user?: AdobeImsSessionUser | null;
  onClose: () => void;
}
const TABS: {
  key: ManageTabId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  key: 'pending-requests',
  label: 'Pending Requests',
  icon: <Inbox size={16} data-id="element-681" data-mp="28311el788im7" />
}, {
  key: 'users',
  label: 'Users',
  icon: <Users size={16} data-id="element-682" data-mp="10ofrk8kmz1ix" />
}, {
  key: 'access-requests',
  label: 'Access Requests',
  icon: <ShieldCheck size={16} data-id="element-683" data-mp="wl9pld4k3wi9" />
}, {
  key: 'audit-log',
  label: 'Audit Log',
  icon: <ScrollText size={16} data-id="element-684" data-mp="1nyk8ff272x4e" />
}, {
  key: 'tracker-log',
  label: 'Tracker Log',
  icon: <Activity size={16} data-id="element-685" data-mp="1nwh8a27bvnly" />
}];
export function ManageModal({
  accessLevel,
  user,
  onClose
}: ManageModalProps) {
  const visibleTabs = visibleManageTabs(accessLevel);
  const [activeTab, setActiveTab] = useState<ManageTabId>(visibleTabs[0] ?? 'pending-requests');
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-4 px-2" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-686" data-mp="108zg45k5inig">
      <div className="relative bg-white rounded-xl shadow-2xl w-full flex flex-col" style={{
      maxWidth: '98vw',
      height: '95vh'
    }} onClick={e => e.stopPropagation()} data-id="element-687" data-mp="mbkl8lb4pnj3">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200" data-id="element-688" data-mp="2o70f63yf5fd4">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-689" data-mp="2j7e3hyy8o76f">Manage</h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-690" data-mp="2j5ks66cokys3">
            <X size={20} data-id="element-691" data-mp="3bvt59jsd0szt" />
          </button>
        </div>

        {/* Body: sidebar + content */}
        <div className="flex flex-1 min-h-0" data-id="element-692" data-mp="36xrmz12rcxpe">
          {/* Left sidebar */}
          <nav className="w-48 border-r border-slate-200 bg-slate-50 flex-shrink-0" data-id="element-693" data-mp="32ulinyz7hglk">
            <div className="py-3" data-id="element-694" data-mp="2xx3aiat0j5em">
              {TABS.filter(tab => canSeeManageTab(accessLevel, tab.key)).map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-695" data-mp="1rh9c3gc2ajvp">
                  {tab.icon}
                  {tab.label}
                  {tab.key === 'pending-requests' && unassignedCount > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold" data-id="element-696" data-mp="kpc3lxuuemn1">
                      {unassignedCount}
                    </span>}
                </button>)}
            </div>
          </nav>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-697" data-mp="2wg67klhk4a63">
            {activeTab === 'pending-requests' && canSeeManageTab(accessLevel, 'pending-requests') && <PendingRequestsTab actor={user ? {
            email: user.email ?? '',
            displayName: user.displayName ?? null
          } : null} onUnassignedCount={setUnassignedCount} data-id="element-698" data-mp="30it3axhcmf8k" />}
            {activeTab === 'users' && canSeeManageTab(accessLevel, 'users') && <UsersTab data-id="element-699" data-mp="2f2wmizmjurt0" />}
            {activeTab === 'access-requests' && canSeeManageTab(accessLevel, 'access-requests') && <AccessRequestsTab data-id="element-700" data-mp="2a43azcqtpavh" />}
            {activeTab === 'audit-log' && canSeeManageTab(accessLevel, 'audit-log') && <AuditLogTab data-id="element-701" data-mp="1hh8sumhhanl6" />}
            {activeTab === 'tracker-log' && canSeeManageTab(accessLevel, 'tracker-log') && <TrackerLogTab data-id="element-702" data-mp="1w5v5uhjmv398" />}
          </div>
        </div>
      </div>
    </div>;
}